// Settings > Spending (v4 V4-SP): what you spent today and this week, and the three limits you set yourself: a daily
// limit, a limit per video, and the amount over which the Director asks first (default 50). Phone first: one column of
// rows, each a number box with its own Save. The Director can do the same by voice ("set my daily limit to 300").
import { useState } from "react";
import { director } from "../director";
import { kindWord, loadSpending, parseLimit, saveLimits, type SpendingView } from "../spending";
import { bump, toast, useRefresh } from "../store";
import { ErrorNote, fmtCredits, Spinner, useLoad } from "./bits";

type Which = "daily" | "project" | "autoOk";
const ROWS: { which: Which; label: string; hint: (v: SpendingView) => string; empty: string }[] = [
  { which: "daily", label: "Daily limit", hint: (v) => (v.limits.daily === null ? "No limit" : `${fmtCredits(v.today.left)} left today`), empty: "No limit" },
  { which: "project", label: "Limit per video", hint: (v) => (v.limits.project === null ? "No limit" : "Across everything made for one video"), empty: "No limit" },
  {
    which: "autoOk",
    label: "Ask me before anything over",
    hint: (v) => (v.limits.autoOk === null ? `${v.autoOkDefault} credits, the default. Video always asks.` : "Video always asks."),
    empty: "",
  },
];

export function SpendingPanel() {
  const tick = useRefresh("credits", "spending");
  const { data, setData, error, loading, reload } = useLoad(loadSpending, [tick]);
  return (
    <section className="group spending" aria-labelledby="spend-h" data-help="settings.spending">
      <h2 id="spend-h" className="group-title">
        Spending
      </h2>
      <p className="muted">Limits you set for yourself. Nothing that would go past one starts. Or just say “keep me under 300 a day”.</p>
      {error && <ErrorNote text={`Couldn't load your spending. ${error}`} retry={reload} />}
      {loading && !data && <Spinner label="Loading" />}
      {data && (
        <>
          <div className="spend-totals">
            <SpendTotal label="Today" total={data.today.total} kinds={data.today.byKind} />
            <SpendTotal label="This week" total={data.week.total} kinds={data.week.byKind} />
          </div>
          <div className="spend-rows">
            {ROWS.map((r) => (
              <LimitRow key={r.which} row={r} view={data} onSaved={setData} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function SpendTotal({ label, total, kinds }: { label: string; total: number; kinds: SpendingView["today"]["byKind"] }) {
  const top = kinds.filter((k) => k.credits > 0).slice(0, 3);
  return (
    <div className="spend-total">
      <span className="spend-total-label">{label}</span>
      <strong className="spend-total-n">{fmtCredits(total)}</strong>
      <span className="muted spend-total-kinds">{top.length ? top.map((k) => `${kindWord(k.kind)} ${fmtCredits(k.credits)}`).join(" · ") : "Nothing yet"}</span>
    </div>
  );
}

function LimitRow({ row, view, onSaved }: { row: (typeof ROWS)[number]; view: SpendingView; onSaved: (v: SpendingView) => void }) {
  const cur = row.which === "autoOk" ? view.limits.autoOk : view.limits[row.which];
  const [text, setText] = useState(cur === null ? "" : String(cur));
  const [busy, setBusy] = useState(false);
  const parsed = parseLimit(text, row.which);
  const changed = parsed !== undefined && parsed !== cur;
  const id = `limit-${row.which}`;
  const save = async () => {
    if (parsed === undefined) return toast("Use a whole number of credits, or leave it empty.", "bad");
    setBusy(true);
    try {
      const v = await saveLimits({ [row.which]: parsed });
      onSaved(v);
      bump("spending");
      const words = row.which === "autoOk" ? (parsed === null ? `ask before anything over ${v.autoOkDefault} credits (the default)` : `ask before anything over ${parsed} credits`) : parsed === null ? `no ${row.label.toLowerCase()}` : `${row.label.toLowerCase()} ${parsed} credits`;
      toast(`Saved: ${words}.`, "good");
      director.context(`The user changed their spending limits in Settings: ${words}.`);
    } catch (e) {
      toast(`Couldn't save it. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="spend-row"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <label htmlFor={id} className="spend-row-label">
        <strong>{row.label}</strong>
        <span className="muted">{row.hint(view)}</span>
      </label>
      <span className="spend-row-input">
        <input
          id={id}
          className="input"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          placeholder={row.which === "autoOk" ? String(view.autoOkDefault) : row.empty}
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-invalid={parsed === undefined}
        />
        <span className="muted spend-unit">credits</span>
        <button data-action="spending.save" type="submit" className="btn btn-quiet btn-small" disabled={!changed || busy}>
          {busy ? "Saving" : "Save"}
        </button>
      </span>
    </form>
  );
}
